export const WORKSPACE_STATUSES = ['active', 'suspended', 'archived'] as const

export type WorkspaceStatus = (typeof WORKSPACE_STATUSES)[number]

export type WorkspaceOperationContext = Readonly<{
  purpose: string
  correlationId: string
}>

export type WorkspaceContext = WorkspaceOperationContext & Readonly<{
  workspaceId: string
}>

export type WorkspaceRecord = Readonly<{
  id: string
  slug: string
  name: string
  status: WorkspaceStatus
  locale: string
  timezone: string
  branding: Record<string, unknown>
  owner_user_id: string
  created_by: string
  created_at: string
  updated_at: string
  archived_at: string | null
}>

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function normalizeWorkspaceOperationContext(input: WorkspaceOperationContext): WorkspaceOperationContext {
  const purpose = input.purpose.trim()
  const correlationId = input.correlationId.trim()

  if (purpose.length < 2 || purpose.length > 120) {
    throw new Error('purpose must contain 2-120 characters')
  }
  if (correlationId.length < 8 || correlationId.length > 160) {
    throw new Error('correlationId must contain 8-160 characters')
  }

  return Object.freeze({ purpose, correlationId })
}

export function createWorkspaceOperationContext(input: WorkspaceOperationContext): WorkspaceOperationContext {
  return normalizeWorkspaceOperationContext(input)
}

export function createWorkspaceContext(input: WorkspaceContext): WorkspaceContext {
  const workspaceId = input.workspaceId.trim()
  if (!UUID_PATTERN.test(workspaceId)) throw new Error('workspaceId must be a canonical UUID')

  const operation = normalizeWorkspaceOperationContext(input)
  return Object.freeze({ workspaceId, ...operation })
}

export function canTransitionWorkspace(from: WorkspaceStatus, to: WorkspaceStatus): boolean {
  if (from === to) return true
  if (from === 'archived') return false
  if (to === 'archived') return true
  return (from === 'active' && to === 'suspended') || (from === 'suspended' && to === 'active')
}
